import { DeleteImageIcon } from 'components/v2/icons';
import { useField, useFormikContext } from 'formik';
import { UploadButton } from 'pages/wizard/WizardStep2';
import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { NoImages } from './NoImages';

type Props = {
  name: string;
  maxFileSizeMB?: number;
  allowedTypes?: string[];
  disabled?: boolean;
};

const defaultTypes = ['image/jpeg', 'image/png', 'image/jpg'];

export const FormikImageUpload: React.FC<Props> = ({ name, maxFileSizeMB = 1, allowedTypes = defaultTypes, disabled }) => {
  const [field, meta] = useField(name);
  const [showModal, setShowModal] = useState(false);
  const { setFieldValue, setFieldError, setFieldTouched } = useFormikContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const value = field.value as File | string | null;
  const error = meta.touched && meta.error;

  const isFile = value instanceof File;
  const isUrl = typeof value === 'string';

  const handleFile = (file: File) => {
    if (!allowedTypes.includes(file.type)) {
      setFieldError(name, 'Only PNG and JPEG images are allowed');
      return;
    }

    const maxSize = maxFileSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      setFieldError(name, `Max file size is ${maxFileSizeMB}MB`);
      return;
    }

    setFieldValue(name, file);

    // hack to trigger validation after setting the file, timeout is used to ensure the value is set before validation
    // File inputs don't fire onBlur the way other input elements do, especially when hidden and triggered programmatically.
    setTimeout(() => setFieldTouched(name, true), 300);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const removeFile = () => {
    setFieldValue(name, null);
    setTimeout(() => setFieldTouched(name, true), 300);
  };

  const getPreviewUrl = () => {
    if (isFile) return URL.createObjectURL(value as File);
    if (isUrl) return value as string;
    return '';
  };

  const previewUrl = getPreviewUrl();

  const fileInputElement = (
    <input
      type="file"
      accept={allowedTypes.join(',')}
      style={{ display: 'none' }}
      ref={inputRef}
      onChange={(e) => {
        if (e.target.files && e.target.files.length > 0) {
          handleFile(e.target.files[0]);
        }
      }}
    />
  );

  return (
    <>
      <ImagesPanel disabled={false} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={handleClick}>
        {!value ? (
          <NoImages inputElement={fileInputElement} disabled={disabled} />
        ) : (
          <ImageBox>
            <ImageBoxImage>
              <img src={getPreviewUrl()} alt="Preview" onClick={() => setShowModal(true)} />
            </ImageBoxImage>
            <br />
            <ImageRemove onClick={removeFile}>
              <DeleteImageIcon />
            </ImageRemove>
          </ImageBox>
        )}
      </ImagesPanel>
      {error && <span className="p-error">{error}</span>}
      {/* Modal for full-size preview */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => setShowModal(false)}
            onKeyUp={(e) => {
              if (['Escape', 'Enter', ' '].includes(e.key)) {
                setShowModal(false);
              }
            }}
            style={{
              all: 'unset', // removes default button styling
              cursor: 'zoom-out',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              outline: 'none',
            }}
            autoFocus
          >
            <img
              src={previewUrl}
              alt="Full preview"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                boxShadow: '0 0 10px #fff',
                borderRadius: '8px',
              }}
            />
          </button>
        </div>
      )}
    </>
  );
};

const InnerBox = styled.div`
  width: auto;
  min-width: 80px;
  max-width: 300px;
  height: 80px;
  border-radius: 4px;
  border: 0.5px solid ${(props) => props.theme.colors.elementsInputBorder};
  position: relative;
  display: flex;
  margin: 7px;
`;

const ImagesPanel = styled.div<{ disabled?: boolean; anyImages?: boolean; dragActive?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  padding: 7px;
  min-height: 106px;
  border-width: 1px;
  border-style: ${(props) => (!props.disabled && !props.anyImages ? 'dashed' : 'solid')};
  border-color: ${(props) =>
    props.disabled
      ? props.theme.colors.elementsFilledInput
      : props.anyImages
      ? props.theme.colors.elementsInputBorder
      : props.theme.colors.secondaryLightGreen};

  background-color: ${(props) =>
    props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};
  color: ${(props) => (props.disabled ? props.theme.colors.primaryBlack50 : props.theme.colors.primaryGreen)};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.28px;
  border-radius: 4px;
  transform-style: preserve-3d;

  ${(props) =>
    props.dragActive &&
    css`
      && > ${InnerBox}, ${UploadButton} {
        transform: translateZ(-10px);
        pointer-events: none;
      }
      opacity: 0.5;
      animation: pulsate 0.1s infinite alternate;
      border-color: black;
      @keyframes pulsate {
        0% {
          border-style: dashed;
        }
        100% {
          border-style: dotted;
        }
      }
    `}
`;

const ImageBox = styled(InnerBox)`
  flex-wrap: wrap;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.primaryWhite};
`;

const ImageBoxImage = styled.div`
  flex: 0 0 auto;
  align-self: center;
  width: auto;
  height: 78px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 90%;
    max-height: 90%;
  }
`;

const ImageRemove = styled.div`
  top: 1px;
  position: absolute;
  right: 5px;
  cursor: pointer;
`;
